import React, { useEffect, useState } from 'react'
import { cloneDeep } from 'lodash'
import { Trans, t } from '@lingui/macro'
import userSearch from '../../../operations/userSearch'
import {
  ButtonPrimary,
  ButtonClean,
  Switch,
  Modal
} from 'front-end/src/components'
import MembersSelector, {
  IMembersSelectorGroup,
  IMembersSelectorMembers,
  IMembersSelectorUser
} from '../../../components/common/members-selector/MembersSelector'
import { notification } from '../../../components/common/notifications/Notifications'
import DrawerItem from '../../../components/common/page/drawer/DrawerItem'
import Loader from '../../../components/common/loader/Loader'
import DateTimePickers from '../../../components/common/date-time-pickers/DateTimePickers'
import IconPlus from '../../../components/common/icon/IconPlusInCircle'
import IconMinus from '../../../components/common/icon/IconMinus'
import Textarea from '../../../components/common/form/textarea/Textarea'
import { useListGroupsQuery } from './PathwayShareModal.codegen'
import { SenderSelect } from '../../manage-campaign-page/campaign-new-action/SenderSelect'

import './GenericAccessModal.less'
import { Dayjs } from 'dayjs'

interface IForm {
  dateTime: Dayjs | null
  message: string
}

export enum AccessModalMode {
  ENROL_AND_ACCESS,
  ONLY_ACCESS
}

interface GenericVariables {
  targetDate: string | null
  message: string | null
  resetEnrolments: boolean
  sendEmail: boolean
}

interface UserVariables extends GenericVariables {
  userIds: number[]
}

interface GroupVariables extends GenericVariables {
  groupIds: number[]
}

interface AccessModalProps {
  show: boolean
  mode: AccessModalMode
  header: string
  subheader?: string
  shareWithUsers?: (variables: UserVariables) => Promise<void>
  shareWithGroups?: (variables: GroupVariables) => Promise<void>
  addUser?: (userId: number) => Promise<void>
  addGroup?: (groupId: number) => Promise<void>
  onCancel?: () => void
  onSubmitted?: () => void
  membersSelected?: IMembersSelectorMembers
}

const emptyMembers = {
  users: [],
  groups: []
}
// TODO split into GenericAccessModal and GenericShareModal
const GenericAccessModal = ({
  show,
  mode,
  addUser,
  addGroup,
  shareWithGroups,
  shareWithUsers,
  onCancel,
  onSubmitted,
  header,
  subheader
}: AccessModalProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const [search, setSearch] = useState<string>('')
  const [showCustomMessage, setShowCustomMessage] = useState<boolean>(false)
  const [resetEnrolments, setResetEnrolments] = useState<boolean>(false)
  const [sendShareEmail, setSendShareEmail] = useState<boolean>(true)
  const [membersSearched, setMembersSearched] =
    useState<IMembersSelectorMembers>(cloneDeep(emptyMembers))
  const [membersSelected, setMembersSelected] =
    useState<IMembersSelectorMembers>(cloneDeep(emptyMembers))
  const [emailIdentityId, setEmailIdentityId] = useState<number | null>(null)
  const [form, setForm] = useState<IForm>({
    dateTime: null,
    message: ''
  })

  useEffect(() => {
    if (search) {
      fetchUsers()
    } else {
      setMembersSearched(cloneDeep(emptyMembers))
      setIsSearching(false)
    }
  }, [search])

  // get all groups
  const { data: groupsList, loading: groupsLoading } = useListGroupsQuery()

  if (groupsLoading) {
    return <Loader />
  }

  const memberGroups = groupsList?.groups
  const memberUsers = []

  const fetchUsers = async () => {
    try {
      const filters = {
        filterUsers: [
          { name: 'notArchived' },
          { name: 'search', value: search }
        ]
      }

      const searchedUsers = await userSearch(filters)

      if (memberGroups) {
        const searchedGroups = memberGroups.filter((row) => {
          return row.name.toLowerCase().includes(search.toLowerCase())
        })

        setMembersSearched({ users: searchedUsers, groups: searchedGroups })
      }
    } catch (err) {
      notification.error(t`Search failed`)
      console.error(err)
    } finally {
      setIsSearching(false)
    }
  }

  const handleAdd = (
    user?: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ): void => {
    const tempList = membersSelected

    if (user) {
      tempList.users.push(user)
    }

    if (group) {
      tempList.groups.push(group)
    }

    setMembersSelected({ ...tempList })
  }

  const handleRemove = (
    user?: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ): void => {
    const tempList = membersSelected

    if (user) {
      tempList.users.splice(
        tempList.users.findIndex((i) => i.id === user.id),
        1
      )
    }
    if (group) {
      tempList.groups.splice(
        tempList.groups.findIndex((i) => i.id === group.id),
        1
      )
    }

    setMembersSelected({ ...tempList })
  }

  const handleHidden = (): void => {
    setShowCustomMessage(false)
    setMembersSelected(cloneDeep(emptyMembers))
    setForm({
      dateTime: null,
      message: ''
    })
  }

  const handleFormChange = (formData: Partial<IForm>): void => {
    setForm({
      ...form,
      ...formData
    })
  }

  const handleToggleMessage = (showCustomMessageVal: boolean): void => {
    setShowCustomMessage(showCustomMessageVal)
    setForm({
      ...form,
      message: ''
    })
  }

  const handleSubmit = async (share = false): Promise<void> => {
    setSubmitting(true)

    try {
      if (share) {
        const commonVariables = {
          targetDate: form.dateTime ? form.dateTime.toISOString() : null,
          message: form.message ? form.message : null,
          resetEnrolments,
          sendEmail: sendShareEmail,
          emailIdentityId
        }

        const userIds = membersSelected.users.map((user) => user.id)
        if (userIds.length > 0) {
          const variables = {
            userIds,
            ...commonVariables
          }

          shareWithUsers && (await shareWithUsers(variables))
        }

        const groupIds = membersSelected.groups.map((group) => group.id)
        if (groupIds.length > 0) {
          const variablesGroups = {
            groupIds,
            ...commonVariables
          }
          shareWithGroups && (await shareWithGroups(variablesGroups))
        }
      } else {
        const addUsersPromises = membersSelected.users
          .map((user) => {
            return addUser && addUser(user.id)
          })
          .filter((v) => v) as Array<Promise<any>>

        const addGroupsPromises = membersSelected.groups
          .map((group) => {
            return addGroup && addGroup(group.id)
          })
          .filter((v) => v) as Array<Promise<any>>

        const promises: Array<Promise<any>> = [
          ...addUsersPromises,
          ...addGroupsPromises
        ]

        await Promise.all(promises)
      }
      onSubmitted && onSubmitted()
    } catch (e) {
      // Avoid this being catched by Sentry
    } finally {
      setSubmitting(false)
    }
  }

  const renderControls = () => {
    switch (mode) {
      case AccessModalMode.ENROL_AND_ACCESS:
        return (
          <>
            <ButtonClean onClick={onCancel}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              onClick={() => handleSubmit(true)}
              isLoading={submitting}
            >
              <Trans>Share</Trans>
            </ButtonPrimary>
          </>
        )

      case AccessModalMode.ONLY_ACCESS:
        return (
          <>
            <ButtonClean onClick={onCancel}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              onClick={() => handleSubmit()}
              isLoading={submitting}
            >
              <Trans>Add access</Trans>
            </ButtonPrimary>
          </>
        )
    }
  }

  return (
    <Modal
      header={header}
      subHeader={subheader}
      show={show}
      onHide={onCancel}
      onShown={() => setSubmitting(false)}
      onHidden={() => handleHidden()}
      controls={renderControls()}
      className="GenericAccessModal"
    >
      <MembersSelector
        isSearching={isSearching}
        addressBook={{ users: memberUsers, groups: memberGroups ?? [] }}
        membersSelected={membersSelected}
        membersSearched={membersSearched}
        onAdd={(user, group) => handleAdd(user, group)}
        onRemove={(user, group) => handleRemove(user, group)}
        onQueryChange={(value) => {
          if (value.length > 0) {
            setIsSearching(true)
          }
          setSearch(value)
        }}
      />
      {mode === AccessModalMode.ENROL_AND_ACCESS && (
        <>
          <DateTimePickers
            value={form.dateTime}
            label={t`Target completion date (optional)`}
            onChange={(dateTime: Dayjs | null) =>
              handleFormChange({ dateTime })
            }
          />
          <div className="toggleRow">
            <DrawerItem
              label={t`Reset progress`}
              hint={
                <Trans>
                  When disabled, sharing will create new enrolments for all
                  selected users and will aim to maintain their progress, while
                  questions with incorrect answers and failed resources will be
                  available again. <br />
                  When enabled, new clear enrolments will be created and users
                  will need to start again.
                </Trans>
              }
            >
              <Switch
                data-test="ResetProgressToggle"
                isChecked={resetEnrolments}
                onChange={() => setResetEnrolments(!resetEnrolments)}
              />
            </DrawerItem>
          </div>
          <div className="toggleRow">
            <DrawerItem label={t`Send share emails`}>
              <Switch
                data-test="ShareEmailsToggle"
                isChecked={sendShareEmail}
                onChange={() => {
                  setSendShareEmail(!sendShareEmail)
                  handleToggleMessage(false)
                }}
              />
            </DrawerItem>
          </div>

          <div
            className={`custom-message ${
              sendShareEmail ? 'custom-message__show' : 'custom-message__hide'
            }`}
          >
            <SenderSelect
              value={emailIdentityId}
              onChange={(v) => setEmailIdentityId(v)}
            />

            <ButtonClean
              size="sm"
              onClick={() => handleToggleMessage(!showCustomMessage)}
              className="custom-message__toggle"
              leftIcon={showCustomMessage ? <IconMinus /> : undefined}
              rightIcon={!showCustomMessage ? <IconPlus /> : undefined}
            >
              {showCustomMessage ? (
                <Trans>Remove message</Trans>
              ) : (
                <Trans>Add message</Trans>
              )}
            </ButtonClean>
            <div className={showCustomMessage ? 'input-show' : 'input-hide'}>
              <Textarea
                placeholder={t`Add a message...`}
                value={form.message}
                onChange={(value) => handleFormChange({ message: value })}
                rows={2}
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default GenericAccessModal
